// /src/components/UserTooltip.jsx

import React, { Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { useSecurity } from "../SecurityContext";
import {
  IconLogout,
  IconOnboarding,
  IconUserSettings,
} from "./userTooltip/UserTooltipIcons";

// English comment: lazy load admin panel link to avoid loading admin code for normal users
const LazyAdminPanelLink = lazy(() => import("./AdminPanelLink"));

export default function UserTooltip({
  userHasPermission,
  realUserHasPermission,
  onLogout,
  t,
  logoutOnly = false,
  gateResumeLink = null,
  forceLogout = false,
  chipBorderAttrs = {},
}) {
  // English comment: tooltip visibility and lifecycle are handled by parent
  const { isViewAsActive, stopViewAs, realUserHasPermission: realFromContext } =
    useSecurity();
  const canForUi = userHasPermission;
  const canForReal = realUserHasPermission || realFromContext || userHasPermission;

  const canReadAdmin =
    !logoutOnly && canForUi("admin", "read");
  const canReadSettings =
    !logoutOnly && canForUi("private.tooltip.settings", "read");
  const canLogout =
    forceLogout || canForReal("private.tooltip.logout", "execute");

  const tooltipClassName = [
    "user-tooltip",
    chipBorderAttrs["data-chip-bg"] ? "user-tooltip--rank-tinted" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={tooltipClassName}
      data-resource="private.tooltip"
      data-action="read"
      data-description="Tooltip"
      {...chipBorderAttrs}
    >
      {isViewAsActive && (
        <button
          type="button"
          className="tooltip-link view-as-reset"
          onClick={stopViewAs}
          data-resource="security.viewas"
          data-action="execute"
          data-description="Stop view-as"
        >
          {t("Back to my view")}
        </button>
      )}

      {canReadAdmin && (
        <Suspense fallback={<div className="tooltip-link tooltip-link--loading">{t("Loading...")}</div>}>
          <LazyAdminPanelLink
            data-resource="admin"
            data-action="read"
            data-description="Admin"
          />
        </Suspense>
      )}

      {canReadSettings && (
        <Link
          className="tooltip-link"
          to="/settings/account"
          data-resource="private.tooltip.settings"
          data-action="read"
          data-description="Tooltip → Settings"
        >
          <IconUserSettings className="tooltip-link__icon" />
          <span className="tooltip-link__label">{t("Settings")}</span>
        </Link>
      )}

      {gateResumeLink === "identity" && (
        <Link
          className="tooltip-link"
          to="/GamerIdentityCheck"
          data-resource="private.tooltip.identityCheck"
          data-action="read"
          data-route="/GamerIdentityCheck"
          data-description="Tooltip → Identity check"
        >
          <IconOnboarding className="tooltip-link__icon" />
          <span className="tooltip-link__label">{t("Identity check")}</span>
        </Link>
      )}

      {gateResumeLink === "onboarding" && (
        <Link
          className="tooltip-link"
          to="/members/onboarding"
          data-resource="private.tooltip.onboarding"
          data-action="read"
          data-route="/members/onboarding"
          data-description="Tooltip → Onboarding"
        >
          <IconOnboarding className="tooltip-link__icon" />
          <span className="tooltip-link__label">{t("Onboarding")}</span>
        </Link>
      )}

      {canLogout && (
        <span
          className="tooltip-link logout"
          onClick={onLogout}
          data-resource="private.tooltip.logout"
          data-action="execute"
          data-description="Tooltip → Logout"
        >
          <IconLogout className="tooltip-link__icon" />
          <span className="tooltip-link__label">{t("Logout")}</span>
        </span>
      )}
    </div>
  );
}
